import { useEffect, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { findKey } from '@npi/buddy-utils';

import { OptionTypes } from 'src/components/Dropdown';
import { showErrorToast } from 'src/components/Toasts/toast';
import { PricingOffer } from 'src/contexts/ServiceOrderProvider';
import { GET_PRICING_OFFERS } from 'src/graphql/queries/pricing.query';
import {
    PricingServiceTypes,
    PricingTerms,
} from 'src/pages/AddMerchantPage/components/ServiceType';

import { useTransactionPricing } from '../useTransactionPricing';

export interface UsePricingOfferProps {
    partnerId: number;
    contractTerm: PricingTerms;
    serviceType: string;
}
type UsePricingOfferResponse = {
    pricingOfferTerminalOrOnlineOptions: OptionTypes[];
    transactionPricingOfferOptions: OptionTypes[];
    availablePricingOffers: PricingOffer[];
};
export const usePricingOffer = ({
    partnerId,
    contractTerm,
    serviceType,
}: UsePricingOfferProps): UsePricingOfferResponse => {
    const [availablePricingOffers, setAvailablePricingOffers] = useState<PricingOffer[]>([]);

    const [getAvailablePricingOffers] = useLazyQuery(GET_PRICING_OFFERS, {
        onError: (error) => {
            showErrorToast(error.message);
        },
        onCompleted: (availablePricingOfferResponseData) => {
            setAvailablePricingOffers(availablePricingOfferResponseData?.pricingOffers);
        },
        fetchPolicy: 'no-cache',
    });

    const { activeTransactionPricingOffersForPartner } = useTransactionPricing(partnerId);

    useEffect(() => {
        const pricingServiceType = findKey(PricingServiceTypes, (o) => {
            return o === serviceType;
        });
        const pricingContractTerm = findKey(PricingTerms, (o) => {
            return o === contractTerm;
        });
        // GET PRICING OFFER BASED OFF SERVICE TYPE & CONTRACT TERM FOR PARTNER
        if (
            pricingContractTerm &&
            pricingServiceType &&
            partnerId &&
            // ACQUIRING requires only transaction pricing offers not terminal / online offers
            pricingServiceType !== PricingServiceTypes.ACQUIRING_ONLY
        ) {
            // fetch new available and active pricing offers for selected type and term for this partner
            getAvailablePricingOffers({
                variables: {
                    filter: {
                        type: pricingServiceType,
                        term: pricingContractTerm,
                        partnerId,
                    },
                },
            });
        }
    }, [serviceType, contractTerm, getAvailablePricingOffers, partnerId]);
    // TERMINAL / ONLINE PRICING OFFER
    const pricingOfferTerminalOrOnlineOptions = useMemo(() => {
        return availablePricingOffers
            ?.filter((option) => {
                return option;
            })
            .map((option) => {
                return {
                    value: option?.id?.toString(),
                    label: option?.name,
                    shouldApplyPromo: option?.promos?.find((promo) => {
                        return promo?.applyPromo;
                    })?.applyPromo,
                };
            });
    }, [availablePricingOffers]);
    // TRANSACTION PRICING OFFER
    const transactionPricingOfferOptions = useMemo(() => {
        return activeTransactionPricingOffersForPartner
            .filter((option) => {
                return option;
            })
            .map(({ offerName, id }) => {
                return {
                    value: id.toString(),
                    label: offerName,
                };
            });
    }, [activeTransactionPricingOffersForPartner]);

    return {
        pricingOfferTerminalOrOnlineOptions,
        transactionPricingOfferOptions,
        availablePricingOffers,
    };
};
